import { CropType } from "@campaign/domain/models/crop-type.model";
import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

export enum PathogenTypeEnum {
    HONGO = "hongo",
    BACTERIA = "bacteria",
    VIRUS = "virus",
    INSECTO = "insecto",
}

const AgentSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim(),
        code: z.string().trim(),
        description: z.string().trim(),
        pathogenType: z.enum(PathogenTypeEnum).nullable(),
        cropTypes: z
            .array(
                z.custom<CropType>((val) => val instanceof CropType, {
                    error: "Invalid CropType instance",
                }),
            )
            .optional(),
    })
    .brand<"Agent">();

export type AgentInputProps = z.input<typeof AgentSchema>;
type AgentProps = z.output<typeof AgentSchema>;

export class Agent {
    private constructor(private readonly props: Readonly<AgentProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    get code(): string {
        return this.props.code;
    }

    get description(): string {
        return this.props.description;
    }

    get pathogenType(): PathogenTypeEnum | null {
        return this.props.pathogenType;
    }

    get cropTypes(): CropType[] | undefined {
        return this.props.cropTypes;
    }

    static validate(
        props: Readonly<MaybeValidProps<AgentInputProps>>,
    ): Result<AgentProps, z.ZodError<AgentProps>> {
        const result = AgentSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<AgentInputProps>>,
    ): Result<AgentProps, PartialValid<typeof Agent>> {
        const result = partialSafeParse(AgentSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<AgentInputProps>,
    ): Result<Agent, z.ZodError<AgentProps>> {
        return Agent.validate(props).map(
            (validatedProps) => new Agent(validatedProps),
        );
    }

    static createWithRecovery(
        props: AgentInputProps,
    ): Result<Agent, ModelError<typeof Agent>> {
        return Agent.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<AgentProps> = {
                    id: error.validProps.id,
                    name: "",
                    code: "",
                    description: "",
                    pathogenType: null,
                    cropTypes: undefined,
                };

                const recoveryProps: Unbrand<AgentProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Agent>(
                    new Agent(recoveryProps as AgentProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Agent(validatedProps));
    }

    toCreateProps(): AgentInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
            code: this.props.code,
            description: this.props.description,
            pathogenType: this.props.pathogenType,
            cropTypes: this.props.cropTypes,
        };
    }
}

export interface AgentFilter {
    name?: string;
    category?: string;
    pathogenType?: PathogenTypeEnum[];
    cropTypeId?: number[];
}
